<template>
  <div class="dashboard-body-futuristic">
    <main class="futuristic-container">
      <!-- Sidebar (keeps the section we came from; defaults to Active Projects) -->
      <AppSidebar :activeItem="sidebarActive" />

      <section class="dashboard-card-futuristic dashboard-card-scrollable" style="width: 100%; display: flex; flex-direction: column; gap: 2rem; padding: 2.5rem; overflow-y: auto;">
        
        <!-- Header exactly matching pdfView -->
        <div class="pdf-viewer-wrapper" style="display: flex; flex-direction: column; flex: 1;">
          <Teleport to="#page-action-bar">
            <h1 class="page-bar-title">{{ projectName || 'Loading Project...' }}</h1>
            <div class="page-bar-actions">
              <button class="btn-viewer-action" @click="goBack">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                  <line x1="19" y1="12" x2="5" y2="12"></line>
                  <polyline points="12 19 5 12 12 5"></polyline>
                </svg>
                Back
              </button>
              <button v-if="canEdit" class="btn-viewer-action" :class="{ 'active-tab-btn': activeTab === 'upload-mail' }"
                :aria-pressed="activeTab === 'upload-mail' ? 'true' : 'false'" @click="setTab('upload-mail')">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="17 8 12 3 7 8"></polyline>
                  <line x1="12" y1="3" x2="12" y2="15"></line>
                </svg>
                Upload Documents
              </button>
              <button class="btn-viewer-action" :class="{ 'active-tab-btn': activeTab === 'contact-list' }"
                :aria-pressed="activeTab === 'contact-list' ? 'true' : 'false'" @click="openContactList" :disabled="contactListLoading">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                  <circle cx="9" cy="7" r="4"></circle>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                </svg>
                Contact List
              </button>
              <button class="btn-viewer-action" :class="{ 'active-tab-btn': activeTab === 'manage-team' }"
                :aria-pressed="activeTab === 'manage-team' ? 'true' : 'false'" @click="openManageTeamModal">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                  <circle cx="9" cy="7" r="4"></circle>
                  <line x1="19" y1="8" x2="19" y2="14"></line>
                  <line x1="22" y1="11" x2="16" y2="11"></line>
                </svg>
                Team
              </button>
            </div>
          </Teleport>
          
          <div class="project-board-content" style="flex: 1; margin-top: 1.5rem;">
            
            <!-- Contact List Tab Content -->
            <div v-if="activeTab === 'contact-list'" class="tab-pane-content">
              <div class="contact-modal-grid" :class="{ 'is-read-only': !canEdit }">
                <section v-if="canEdit" class="contact-form-card">
                  <h3>Add Contact</h3>
                  <p>Save a new client contact into the project directory.</p>

                  <form class="contact-form" @submit.prevent="saveContact">
                    <div class="contact-field">
                      <label>Name</label>
                      <input v-model.trim="contactForm.contact_name" type="text" placeholder="Full name" required />
                    </div>
                    <div class="contact-field">
                      <label>Email</label>
                      <input v-model.trim="contactForm.contact_email" type="email" placeholder="name@company.com" required />
                    </div>
                    <div class="contact-field">
                      <label>Mobile</label>
                      <input v-model.trim="contactForm.contact_mobile" type="text" placeholder="Phone number" />
                    </div>

                    <div class="contact-form-actions">
                      <button type="button" class="btn-secondary" @click="loadContactList" :disabled="contactListLoading">
                        {{ contactListLoading ? 'Refreshing...' : 'Refresh' }}
                      </button>
                      <button type="submit" class="btn-primary" :disabled="contactListSaving">
                        {{ contactListSaving ? 'Saving...' : 'Save Contact' }}
                      </button>
                    </div>
                  </form>
                  <p v-if="contactListError" class="contact-error">{{ contactListError }}</p>
                </section>

                <section class="contact-list-card">
                  <div class="contact-list-header">
                    <h3>Saved Contacts</h3>
                    <span>{{ contactList.length }} record(s)</span>
                  </div>
                  <table class="pdf-content-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Email ID</th>
                        <th>Phone No</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="contact in contactList" :key="contact.id">
                        <td>{{ contact.contact_name }}</td>
                        <td>{{ contact.contact_email }}</td>
                        <td>{{ contact.contact_mobile || '--' }}</td>
                      </tr>
                      <tr v-if="!contactList.length && !contactListLoading">
                        <td colspan="3" style="text-align: center; padding: 1.5rem;">No contacts yet.<template v-if="canEdit"> Add one with the form.</template></td>
                      </tr>
                      <tr v-if="contactListLoading">
                        <td colspan="3" style="text-align: center; padding: 1.5rem;">Loading...</td>
                      </tr>
                    </tbody>
                  </table>
                </section>
              </div>
            </div>

            <!-- Upload Documents Tab Content (admin only) -->
            <div v-if="activeTab === 'upload-mail' && canEdit" class="tab-pane-content">
              <div class="upload-pro-wrapper">

                <!-- Heading -->
                <div class="upload-pro-head">
                  <div>
                    <h1 class="upload-pro-title">Upload Documents</h1>
                    <p class="upload-pro-sub">Link contract &amp; project documents to this project</p>
                  </div>
                  <div class="upload-pro-badge">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    Project Linked
                  </div>
                </div>

                <!-- Status message -->
                <transition name="fade-slide">
                  <div v-if="uploadStatus.show" :class="['status-message', 'status-' + uploadStatus.type]">
                    <svg v-if="uploadStatus.type === 'success'" width="18" height="18" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" stroke-width="2.5">
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                        <polyline points="22 4 12 14.01 9 11.01" />
                    </svg>
                    <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                        stroke-width="2.5">
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" y1="8" x2="12" y2="12" />
                        <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    {{ uploadStatus.message }}
                  </div>
                </transition>

                <!-- Two-column body -->
                <div class="upload-pro-grid">

                  <!-- LEFT: info / steps -->
                  <aside class="upload-info-panel">
                    <div class="info-hero-icon">
                      <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                          stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                          <polyline points="14 2 14 8 20 8" />
                          <line x1="16" y1="13" x2="8" y2="13" />
                          <line x1="16" y1="17" x2="8" y2="17" />
                      </svg>
                    </div>
                    <h2 class="info-title">Project Document Intake</h2>
                    <p class="info-desc">
                      Attach contract documents, work orders and other PDFs directly to this project so they stay
                      organised alongside the bid.
                    </p>

                    <ul class="info-steps">
                      <li>
                        <span class="step-no">1</span>
                        <div>
                          <strong>Set the document date</strong>
                          <span>When the document was received or signed</span>
                        </div>
                      </li>
                      <li>
                        <span class="step-no">2</span>
                        <div>
                          <strong>Add your PDF files</strong>
                          <span>Drag &amp; drop or browse — multiple allowed</span>
                        </div>
                      </li>
                      <li>
                        <span class="step-no">3</span>
                        <div>
                          <strong>Upload &amp; link</strong>
                          <span>Documents are saved against this project</span>
                        </div>
                      </li>
                    </ul>

                    <div class="info-meta">
                      <div class="meta-pill">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                            stroke-width="2">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                            <polyline points="14 2 14 8 20 8" />
                        </svg>
                        PDF only
                      </div>
                    </div>
                  </aside>

                  <!-- RIGHT: upload panel -->
                  <div class="upload-action-panel">
                    <!-- Document date -->
                    <div class="field-block">
                      <label class="field-label">Document Date <span class="req">*</span></label>
                      <div class="select-wrap">
                        <svg class="select-icon" width="18" height="18" viewBox="0 0 24 24" fill="none"
                            stroke="currentColor" stroke-width="2">
                            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                            <line x1="16" y1="2" x2="16" y2="6" />
                            <line x1="8" y1="2" x2="8" y2="6" />
                            <line x1="3" y1="10" x2="21" y2="10" />
                        </svg>
                        <input type="date" v-model="mailDate" class="pro-input" @change="dateError = false"
                            :class="{ 'input-error': dateError }">
                      </div>
                    </div>

                    <!-- Drop zone -->
                    <div class="pro-dropzone" :class="{ 'is-dragging': isDragging, 'input-error': fileError }"
                        @click="$refs.mailFileInput.click()" @dragover.prevent="onMailDragOver"
                        @dragleave.prevent="onMailDragLeave" @drop.prevent="onMailDrop">
                      <div class="dz-scanline" v-if="isDragging"></div>
                      <div class="dz-icon">
                        <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                            stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                            <polyline points="17 8 12 3 7 8" />
                            <line x1="12" y1="3" x2="12" y2="15" />
                        </svg>
                      </div>
                      <p class="dz-primary">
                        {{ isDragging ? 'Drop files to add them' : 'Drag & drop your project PDFs here' }}
                      </p>
                      <p class="dz-secondary">or</p>
                      <button class="dz-browse" type="button" @click.stop="$refs.mailFileInput.click()">
                        Browse Files
                      </button>
                      <input type="file" id="mailFileInput" ref="mailFileInput" @change="handleMailFileChange($event)"
                          hidden accept=".pdf" multiple>
                      <p class="dz-hint">Supported: PDF Only</p>
                    </div>

                    <!-- Selected files -->
                    <div v-if="uploadFiles.length" class="files-block">
                      <div class="files-block-head">
                        <span>{{ uploadFiles.length }} file{{ uploadFiles.length > 1 ? 's' : '' }} selected</span>
                        <button class="clear-all" type="button" @click="clearMailFiles">Clear All</button>
                      </div>
                      <ul class="file-cards">
                        <li v-for="(f, idx) in uploadFiles" :key="idx" class="file-card-row">
                          <div class="fc-icon">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                stroke-width="2">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                <polyline points="14 2 14 8 20 8" />
                            </svg>
                          </div>
                          <div class="fc-info">
                            <span class="fc-name">{{ f.name }}</span>
                            <span class="fc-size">{{ formatSize(f.size) }}</span>
                          </div>
                          <button class="fc-remove" type="button" @click="removeMailFile(idx)" title="Remove"
                              :aria-label="'Remove ' + f.name">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                stroke-width="2.5">
                                <line x1="18" y1="6" x2="6" y2="18" />
                                <line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                          </button>
                        </li>
                      </ul>
                    </div>

                    <!-- Submit -->
                    <button class="pro-submit" type="button" @click="saveMailFiles()"
                        :disabled="isUploading || !uploadFiles.length || !mailDate">
                      <svg v-if="isUploading" class="spin" width="20" height="20" viewBox="0 0 24 24" fill="none"
                          stroke="currentColor" stroke-width="2">
                          <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                      </svg>
                      <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                          stroke-width="2">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                          <polyline points="17 8 12 3 7 8" />
                          <line x1="12" y1="3" x2="12" y2="15" />
                      </svg>
                      {{ isUploading ? 'Uploading & Linking…' : 'Upload & Process' }}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Manage Team Tab Content -->
            <div v-if="activeTab === 'manage-team'" class="tab-pane-content inline-team-manage">
              <div class="modal-header">
                <div>
                  <h2>{{ showAssignMemberView ? 'Assign Member' : 'Project Members' }}</h2>
                  <p class="modal-subtitle">
                    {{ showAssignMemberView ? `${availableUsers.length} available company user(s)` : `${projectMembers.length} member(s) assigned to this project` }}
                  </p>
                </div>
                <div class="team-modal-header-actions">
                  <button v-if="showAssignMemberView" type="button" class="btn-secondary" @click="showAssignMemberView = false">Back</button>
                  <button v-else type="button" class="btn-primary admin-only" @click="showAssignMemberView = true" :disabled="teamMembersLoading">Assign Member</button>
                </div>
              </div>
              
              <div class="modal-body team-modal-body">
                <div v-if="teamMembersError" class="contact-error" style="margin-top: 0;">{{ teamMembersError }}</div>
                <div v-if="teamMembersLoading" class="team-member-empty">Loading team members...</div>
                
                <template v-else-if="!showAssignMemberView">
                  <div v-if="projectMembers.length" class="team-member-list">
                    <article v-for="member in projectMembers" :key="member.projectUserId || member.id" class="team-member-card">
                      <div class="team-member-main">
                        <div class="team-member-avatar" :style="{ background: member.color }">{{ member.initials }}</div>
                        <div class="team-member-meta">
                          <strong class="team-member-name">{{ member.name }}</strong>
                          <div class="team-member-detail-row">
                            <span class="team-member-role">{{ member.role }}</span>
                            <span class="team-member-badge">Assigned</span>
                          </div>
                        </div>
                      </div>
                      <button type="button" class="team-member-action team-member-action--remove admin-only" :disabled="teamMembersActionLoading" @click="removeProjectMember(member)">Remove</button>
                    </article>
                  </div>
                  <div v-else class="team-member-empty">No members assigned yet.</div>
                </template>
                
                <template v-else>
                  <div v-if="availableUsers.length" class="team-member-list">
                    <article v-for="user in availableUsers" :key="user.id" class="team-member-card">
                      <div class="team-member-main">
                        <div class="team-member-avatar" :style="{ background: user.color }">{{ user.initials }}</div>
                        <div class="team-member-meta">
                          <strong class="team-member-name">{{ user.name }}</strong>
                          <div class="team-member-detail-row">
                            <span class="team-member-role">{{ user.role }}</span>
                            <span class="team-member-badge team-member-badge--available">Available</span>
                          </div>
                        </div>
                      </div>
                      <div class="team-member-controls" style="display:flex; align-items:center; gap:0.75rem;">
                        <select v-model="userRolesSelections[user.id]" class="role-select-custom" :style="{ border: '1px solid ' + (roleErrors.includes(user.id) ? '#ef4444' : '#e2e8f0') }">
                          <option :value="undefined" disabled>Select Role</option>
                          <option value="Project manager">Project manager</option>
                          <option value="Lead">Lead</option>
                          <option value="General user">General user</option>
                          <option value="QA">QA</option>
                        </select>
                        <input type="checkbox" :value="user.id" v-model="selectedUsersToAssign" class="team-member-checkbox">
                      </div>
                    </article>
                  </div>
                  <div v-else class="team-member-empty">No more users available to assign.</div>
                </template>
              </div>

              <div class="modal-footer team-modal-footer" v-if="showAssignMemberView && availableUsers.length > 0">
                <button type="button" class="btn-primary admin-only" :disabled="selectedUsersToAssign.length === 0 || teamMembersActionLoading" @click="assignSelectedMembers">
                  {{ teamMembersActionLoading ? 'Assigning...' : `Assign Selected (${selectedUsersToAssign.length})` }}
                </button>
              </div>
            </div>

          </div>
        </div>

      </section>
    </main>
  </div>
</template>

<script>
import axios from "axios";
import AppSidebar from './Sidebar.vue';
import { isAdmin } from '@/utils/auth';
import { confirmAction } from '@/utils/confirm';
import { notify } from '@/utils/notify';

const TABS = ['upload-mail', 'contact-list', 'manage-team'];

export default {
  name: "ProjectBoard",
  components: {
    AppSidebar
  },
  props: {
    getAccessToken: Function
  },
  data() {
    return {
      pdfId: this.$route.params.pdf_id,
      projectName: "",
      // Uploading is admin-only, so viewers open on the contact directory.
      activeTab: isAdmin() ? "upload-mail" : "contact-list",
      showAssignMemberView: false,
      contactList: [],
      contactListLoading: false,
      contactListSaving: false,
      contactListError: "",
      companyUsers: [],
      projectUsers: [],
      teamMembersLoading: false,
      teamMembersActionLoading: false,
      teamMembersError: "",
      contactForm: {
        contact_name: "",
        contact_email: "",
        contact_mobile: ""
      },
      userRolesSelections: {},
      selectedUsersToAssign: [],
      roleErrors: [],
      // Upload Documents UI Data
      uploadFiles: [],
      mailDate: "",
      dateError: false,
      fileError: false,
      isDragging: false,
      isUploading: false,
      uploadStatus: {
        show: false,
        message: "",
        type: "success"
      }
    };
  },
  computed: {
    canEdit() {
      return isAdmin();
    },
    // Keep the sidebar on whatever section we were opened from; default to the
    // post-bid "Active Projects" section.
    sidebarActive() {
      const from = String(this.$route.query.from || '').trim();
      const allowed = ['tenders', 'bookmarks', 'files', 'upload', 'active', 'completed', 'knowledge', 'admin', 'dashboard'];
      return allowed.includes(from) ? from : 'active';
    },
    projectMemberIds() {
      return new Set(this.projectUsers.map((projectUser) => String(projectUser.user_id || projectUser.id || projectUser.userId)));
    },
    projectMembers() {
      return this.companyUsers
        .map((user, index) => {
          const userId = String(user.id);
          const projectUser = this.projectUsers.find((item) => String(item.user_id || item.id || item.userId) === userId);
          if (!projectUser) return null;
          return {
            id: user.id,
            projectUserId: projectUser.project_user_id || projectUser.id || null,
            name: user.name,
            initials: this.getInitials(user.name),
            role: projectUser.role || this.getUserRoleLabel(user),
            color: this.getMemberColor(index)
          };
        })
        .filter(Boolean);
    },
    availableUsers() {
      return this.companyUsers
        .filter((user) => !this.projectMemberIds.has(String(user.id)))
        .map((user, index) => ({
          id: user.id,
          name: user.name,
          initials: this.getInitials(user.name),
          role: this.getUserRoleLabel(user),
          color: this.getMemberColor(index)
        }));
    }
  },
  mounted() {
    this.fetchProjectInfo();
    this.loadContactList();
    this.loadManageTeamData();
    const tab = String(this.$route.query.tab || '');
    if (TABS.includes(tab) && (tab !== 'upload-mail' || this.canEdit)) {
      this.activeTab = tab;
    }
  },
  methods: {
    goBack() {
      // Back always lands on this project's Bid Details, Project tab. When we
      // came straight from there, step back in history instead of stacking a
      // new entry; otherwise open it, carrying the status implied by `from`
      // so the sidebar keeps the section we came from.
      const back = (window.history.state && window.history.state.back) || '';
      if (back.split('?')[0] === `/bid-details/${this.pdfId}`) {
        this.$router.back();
        return;
      }
      const from = this.sidebarActive;
      const query = { tab: 'project' };
      if (from === 'completed') query.status = 'COMPLETED';
      else if (from === 'active') query.status = 'WIN';
      this.$router.push({ name: 'BidDetails', params: { pdf_id: this.pdfId }, query });
    },
    setTab(tab) {
      this.activeTab = tab;
    },
    getAuthHeaders() {
      return { Authorization: "Bearer " + this.getAccessToken() };
    },
    getInitials(name = "") {
      const parts = name.trim().split(/\s+/).filter(Boolean);
      if (!parts.length) return "NA";
      if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
      return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
    },
    getMemberColor(index) {
      const palette = ["#0f4c81", "#0071c5", "#0f766e", "#d97706", "#7c3aed", "#be123c"];
      return palette[index % palette.length];
    },
    getUserRoleLabel(user) {
      if (Array.isArray(user.roles) && user.roles.length) {
        return user.roles[0].name || "Member";
      }
      return "Member";
    },
    async fetchProjectInfo() {
      try {
        const res = await axios.get(this.$API_URL + `/user/get-pdf-deatil?pdf_id=${this.pdfId}`, {
          headers: this.getAuthHeaders()
        });
        const project = res.data || {};
        const name = project.project_name || project.pdf_name;
        if (name) {
          this.projectName = name.replace(/\.pdf/gi, "").replace(/[-_]/g, " ");
        }
      } catch (e) {
        console.error("Error fetching project info", e);
      }
    },
    openContactList() {
      this.activeTab = "contact-list";
      this.loadContactList();
    },
    async loadContactList() {
      if (!this.pdfId) return;
      this.contactListLoading = true;
      try {
        const res = await axios.get(this.$API_URL + `/company_admin/client-contact-details?project_id=${this.pdfId}`, {
          headers: this.getAuthHeaders()
        });
        this.contactList = res.data.items || [];
      } catch (err) {
        console.error("Load contacts failed", err);
      } finally {
        this.contactListLoading = false;
      }
    },
    async saveContact() {
      if (!this.canEdit) return;
      this.contactListSaving = true;
      this.contactListError = "";
      try {
        await axios.post(this.$API_URL + "/company_admin/save-client-contact-detai", {
          ...this.contactForm,
          project_id: Number(this.pdfId)
        }, { headers: this.getAuthHeaders() });
        this.contactForm = { contact_name: "", contact_email: "", contact_mobile: "" };
        notify.success("Contact saved");
        this.loadContactList();
      } catch (err) {
        this.contactListError = "Couldn’t save the contact. Check the details and try again.";
      } finally {
        this.contactListSaving = false;
      }
    },
    async openManageTeamModal() {
      this.activeTab = "manage-team";
      await this.loadManageTeamData();
    },
    closeManageTeamModal() {
      this.showAssignMemberView = false;
      this.selectedUsersToAssign = [];
      this.userRolesSelections = {};
      this.roleErrors = [];
    },
    async loadManageTeamData() {
      this.teamMembersLoading = true;
      this.teamMembersError = "";
      try {
        const [companyRes, projectRes] = await Promise.all([
          axios.get(this.$API_URL + "/company_admin/users?page=1&page_size=1000", { headers: this.getAuthHeaders() }),
          axios.get(this.$API_URL + `/management/projects/${this.pdfId}/users`, { headers: this.getAuthHeaders() })
        ]);
        this.companyUsers = companyRes.data.items || [];
        this.projectUsers = projectRes.data.items || [];
      } catch (err) {
        this.teamMembersError = "Couldn’t load the team. Refresh the page to try again.";
      } finally {
        this.teamMembersLoading = false;
      }
    },
    async assignSelectedMembers() {
      if (this.$isViewOnly()) return;   // viewers cannot assign members
      this.roleErrors = [];
      let hasErrors = false;
      for (const userId of this.selectedUsersToAssign) {
        if (!this.userRolesSelections[userId]) {
          this.roleErrors.push(userId);
          hasErrors = true;
        }
      }
      if (hasErrors) return;
      this.teamMembersActionLoading = true;
      try {
        const promises = this.selectedUsersToAssign.map(userId => {
          return axios.post(this.$API_URL + `/management/projects/${this.pdfId}/users`, {
            project_id: Number(this.pdfId),
            user_id: Number(userId),
            role: this.userRolesSelections[userId]
          }, { headers: this.getAuthHeaders() });
        });
        await Promise.all(promises);
        this.showAssignMemberView = false;
        await this.loadManageTeamData();
      } catch (err) {
        this.teamMembersError = "Couldn’t assign the selected members. Try again.";
      } finally {
        this.teamMembersActionLoading = false;
      }
    },
    async removeProjectMember(member) {
      if (this.$isViewOnly()) return;   // viewers cannot remove members
      if (!member.projectUserId) return;
      const ok = await confirmAction({
        title: `Remove ${member.name} from This Project?`,
        message: 'They lose access to this project. You can assign them again later.',
        confirmLabel: 'Remove Member',
        destructive: true,
      });
      if (!ok) return;
      this.teamMembersActionLoading = true;
      try {
        await axios.delete(this.$API_URL + `/management/project-users/${member.projectUserId}`, { headers: this.getAuthHeaders() });
        await this.loadManageTeamData();
      } catch (err) {
        this.teamMembersError = "Couldn’t remove the member. Try again.";
      } finally {
        this.teamMembersActionLoading = false;
      }
    },
    // Upload Documents UI Methods
    handleMailFileChange(event) {
      this.addMailFiles(Array.from(event.target.files || []));
      event.target.value = '';
    },
    addMailFiles(files) {
      if (files.length > 0) this.fileError = false;
      files.forEach(file => {
        if (!file.name.toLowerCase().endsWith('.pdf')) {
          this.uploadStatus = { show: true, message: `${file.name} is not a PDF and was skipped.`, type: 'error' };
          setTimeout(() => { this.uploadStatus.show = false; }, 3000);
          return;
        }
        const exists = this.uploadFiles.some(f => f.name === file.name && f.size === file.size);
        if (!exists) this.uploadFiles.push(file);
      });
    },
    onMailDragOver() {
      this.isDragging = true;
    },
    onMailDragLeave() {
      this.isDragging = false;
    },
    onMailDrop(event) {
      this.isDragging = false;
      this.addMailFiles(Array.from(event.dataTransfer.files || []));
    },
    clearMailFiles() {
      this.uploadFiles = [];
    },
    removeMailFile(index) {
      this.uploadFiles.splice(index, 1);
    },
    formatSize(bytes) {
      if (bytes < 1024) return bytes + ' B';
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
      return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
    },
    async saveMailFiles() {
      if (this.$isViewOnly()) return;   // viewers cannot upload mail files
      if (!this.mailDate) {
        this.dateError = true;
        this.uploadStatus = { show: true, message: "Choose the document date first.", type: 'error' };
        setTimeout(() => { this.uploadStatus.show = false; }, 3000);
        return;
      }
      if (!this.uploadFiles.length) {
        this.fileError = true;
        this.uploadStatus = { show: true, message: "Add at least one PDF file.", type: 'error' };
        setTimeout(() => { this.uploadStatus.show = false; }, 3000);
        return;
      }

      this.isUploading = true;
      const formData = new FormData();
      formData.append('project_id', Number(this.pdfId));
      
      // Convert YYYY-MM-DD to ISO format for backend consistency
      const isoDate = new Date(this.mailDate).toISOString();
      formData.append('mail_date', isoDate);

      this.uploadFiles.forEach((file) => {
        formData.append('uploaded_files', file);
      });

      try {
        const url = this.$API_URL + "/company_admin/mail-records/upload-mail-pdf";
        await axios.post(url, formData, {
          headers: {
            'Authorization': 'Bearer ' + this.getAccessToken(),
            'Content-Type': 'multipart/form-data',
            'accept': 'application/json'
          }
        });

        this.uploadStatus = { show: true, message: "Documents uploaded and linked to this project.", type: "success" };
        this.uploadFiles = [];
        this.mailDate = "";
        
        // Optional: Refresh or redirect if needed
        setTimeout(() => { this.uploadStatus.show = false; }, 5000);
      } catch (error) {
        const msg = (error && error.response && error.response.data && error.response.data.message) ? error.response.data.message : error.message;
        this.uploadStatus = { show: true, message: "Upload failed: " + msg + ". Try again.", type: 'error' };
        console.error('Error uploading mail files:', error);
      } finally {
        this.isUploading = false;
      }
    }
  }
};
</script>

<style scoped>
/* Modal Styling */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(10, 15, 28, 0.7);
  backdrop-filter: blur(4px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.modal-content.contact-modal {
  background: white;
  border-radius: 16px;
  width: 92%;
  max-width: 960px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
}

.modal-content.team-modal {
  background: white;
  border-radius: 16px;
  width: 92%;
  max-width: 760px;
  max-height: 82vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
}

/* Tab styling adjustments for inline layouts */
.inline-team-manage {
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03);
  border: 1px solid #e2e8f0;
}
.btn-viewer-action.active-tab-btn {
  background: #dbf1ff;
  color: #0071C5;
  border-color: #0071C5;
}
.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #e2e8f0;
}

.modal-header h2 {
  margin: 0;
  font-size: 1.25rem;
  color: #1e293b;
  font-weight: 600;
  text-align: left;
}

.modal-subtitle {
  margin: 0.2rem 0 0;
  font-size: 0.85rem;
  color: #64748b;
  text-align: left;
}

.btn-close {
  background: transparent;
  border: none;
  font-size: 1.5rem;
  line-height: 1;
  color: #64748b;
  cursor: pointer;
  transition: color 0.2s;
}

.btn-close:hover {
  color: #ef4444;
}

.modal-body {
  padding: 1.5rem;
  overflow-y: auto;
}

.team-modal-body {
  background: linear-gradient(180deg, #f8fbff 0%, #fefefe 100%);
}

.team-modal-header-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.team-modal-header-actions .btn-primary,
.team-modal-header-actions .btn-secondary {
  padding: 0.72rem 1rem;
}

.contact-modal-grid {
  display: grid;
  grid-template-columns: 340px minmax(0, 1fr);
  gap: 1.25rem;
}

.contact-modal-grid.is-read-only {
  grid-template-columns: minmax(0, 1fr);
}

.contact-form-card,
.contact-list-card {
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  background: #f8fafc;
  padding: 1.1rem;
}

.contact-form-card h3,
.contact-list-card h3 {
  margin: 0 0 0.35rem;
  font-size: 1rem;
  color: #0f172a;
  text-align: left;
}

.contact-form-card p {
  margin: 0 0 1rem;
  color: #64748b;
  font-size: 0.9rem;
  text-align: left;
}

.team-modal-footer {
  padding: 1rem 1.5rem;
  border-top: 1px solid #e2e8f0;
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  background: #f8fafc;
  border-radius: 0 0 18px 18px;
}

.role-select-custom {
  font-size: 0.8rem;
  padding: 0.35rem 0.5rem;
  border-radius: 6px;
  background: #fff;
  color: #1e293b;
  min-width: 140px;
  cursor: pointer;
  outline: none;
  transition: all 0.2s;
}

.team-member-checkbox {
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: #2563eb;
}

.contact-form {
  display: grid;
  gap: 0.85rem;
}

.contact-field {
  display: grid;
  gap: 0.35rem;
}

.contact-field label {
  font-size: 0.8rem;
  font-weight: 700;
  color: #334155;
  text-align: left;
}

.contact-field input {
  width: 100%;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  padding: 0.8rem 0.9rem;
  font-size: 0.95rem;
  background: white;
  box-sizing: border-box;
}

.contact-field input:focus {
  outline: none;
  border-color: #0071c5;
  box-shadow: 0 0 0 3px rgba(0, 113, 197, 0.12);
}

.contact-form-actions {
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  margin-top: 0.25rem;
}

.btn-secondary,
.btn-primary {
  border: none;
  border-radius: 10px;
  padding: 0.8rem 1rem;
  font-weight: 700;
  cursor: pointer;
}

.btn-secondary {
  background: #e2e8f0;
  color: #0f172a;
}

.btn-primary {
  background: #0071c5;
  color: white;
}

.btn-secondary:disabled,
.btn-primary:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.contact-list-card {
  background: white;
}

.contact-list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.8rem;
  gap: 0.75rem;
}

.contact-list-header span {
  color: #64748b;
  font-size: 0.85rem;
  font-weight: 600;
}

.contact-error {
  margin: 0.9rem 0 0;
  color: #b91c1c;
  font-size: 0.9rem;
  text-align: left;
}

.team-member-list {
  display: grid;
  gap: 0.9rem;
}

.team-member-card {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.1rem;
  border: 1px solid #dbe7f4;
  border-radius: 16px;
  background: linear-gradient(180deg, #ffffff 0%, #f6faff 100%);
  box-shadow: 0 14px 28px rgba(15, 23, 42, 0.06);
}

.team-member-main {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 0.9rem;
  flex: 1 1 auto;
}

.team-member-avatar {
  width: 44px;
  height: 44px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 0.9rem;
  font-weight: 700;
  flex-shrink: 0;
  box-shadow: 0 12px 24px rgba(15, 76, 129, 0.16);
}

.team-member-meta {
  min-width: 0;
  display: grid;
  gap: 0.28rem;
  text-align: left;
}

.team-member-name {
  color: #0f172a;
  font-size: 1rem;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.team-member-detail-row {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  flex-wrap: wrap;
}

.team-member-role {
  color: #64748b;
  font-size: 0.84rem;
  font-weight: 600;
}

.team-member-badge {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 0.22rem 0.6rem;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  background: #dbeafe;
  color: #1d4ed8;
}

.team-member-badge--available {
  background: #dcfce7;
  color: #15803d;
}

.team-member-action {
  min-width: 118px;
  padding: 0.78rem 1rem;
  border-radius: 12px;
  border: 1px solid transparent;
  font-size: 0.9rem;
  font-weight: 700;
  cursor: pointer;
  transition: transform 0.18s ease, box-shadow 0.18s ease, background 0.18s ease, color 0.18s ease;
}

.team-member-action:hover:not(:disabled) {
  transform: translateY(-1px);
}

.team-member-action:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.team-member-action--assign {
  background: #0071c5;
  color: white;
  box-shadow: 0 10px 18px rgba(0, 113, 197, 0.2);
}

.team-member-action--remove {
  background: #fff1f2;
  border-color: #fecdd3;
  color: #be123c;
}

.team-member-empty {
  border: 1px dashed #cbd5e1;
  border-radius: 16px;
  padding: 1.2rem;
  background: #f8fafc;
  text-align: center;
  color: #64748b;
  font-size: 0.95rem;
}

.pdf-content-table { width: 100%; border-collapse: collapse; text-align: left; }
.pdf-content-table th { text-align: left; background: #f1f5f9; padding: 0.8rem; font-size: var(--fs-th); font-weight: var(--fw-th); text-transform: uppercase; letter-spacing: var(--tracking-th); color: var(--text-secondary); }
.pdf-content-table td { text-align: left; padding: 0.8rem; border-bottom: 1px solid #e2e8f0; font-size: 0.9rem; color: #334155; }

@media (max-width: 900px) {
  .contact-modal-grid { grid-template-columns: 1fr; }
  .modal-content.team-modal { width: 94%; }
  .modal-header { align-items: flex-start; gap: 0.9rem; }
  .team-modal-header-actions { width: 100%; justify-content: flex-end; flex-wrap: wrap; }
  .team-member-card { flex-direction: column; grid-template-columns: 1fr; align-items: stretch; }
  .team-member-action { width: 100%; }
}

/* Upload Documents Specific Styles */
.status-message {
  margin-bottom: 15px;
  padding: 10px 15px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  text-align: center;
  animation: fadeIn 0.3s ease-in-out;
}

.status-success {
  background-color: #ecfdf3;
  color: #047857;
  border: 1px solid rgba(16, 185, 129, 0.2);
}

.status-error {
  background-color: #fef3f2;
  color: #b42318;
  border: 1px solid rgba(239, 68, 68, 0.2);
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
}

.mail-date-input {
  padding: 0.5rem 1rem;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background-color: #fff;
  color: #1e293b;
  font-family: inherit;
  outline: none;
  cursor: pointer;
  transition: all 0.2s ease;
}

.mail-date-input:focus {
  border-color: #4facfe;
  box-shadow: 0 0 0 3px rgba(79, 172, 254, 0.1);
}

/* ============================================================
   Upload Project Documents — styled like the Upload Bids page
   ============================================================ */
.upload-pro-wrapper {
    width: 100%;
    position: relative;
    z-index: 2;
    font-family: var(--font-sans);
}

.upload-pro-head {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 1rem;
    margin-bottom: 1.75rem;
}

.upload-pro-title {
    font-family: var(--font-display);   /* Outfit — page title */
    font-size: var(--fs-h1);            /* 36px page title */
    line-height: var(--lh-h1);          /* 44px */
    font-weight: var(--fw-h1);          /* 700 */
    color: var(--text-primary);
    margin: 0;
    letter-spacing: -0.02em;
}

.upload-pro-sub {
    margin: 0.3rem 0 0;
    color: #64748b;
    font-size: 0.95rem;
    font-weight: 500;
}

.upload-pro-badge {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    background: #ecfdf3;
    color: #047857;
    font-weight: 700;
    font-size: 0.8rem;
    padding: 0.5rem 1rem;
    border-radius: 20px;
}


.status-message {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    margin-bottom: 1.25rem;
    padding: 0.85rem 1.1rem;
    border-radius: 12px;
    font-size: 0.9rem;
    font-weight: 600;
}

.status-success {
    background: rgba(16, 185, 129, 0.1);
    color: #059669;
    border: 1px solid rgba(16, 185, 129, 0.2);
}

.status-error {
    background: #fef3f2;
    color: #b42318;
    border: 1px solid rgba(239, 68, 68, 0.2);
}

.fade-slide-enter-active,
.fade-slide-leave-active {
    transition: all 0.3s ease;
}

.fade-slide-enter-from,
.fade-slide-leave-to {
    opacity: 0;
    transform: translateY(-8px);
}

.upload-pro-grid {
    display: grid;
    grid-template-columns: 0.85fr 1.15fr;
    gap: 1.5rem;
    align-items: stretch;
}

.upload-info-panel {
    background: linear-gradient(155deg, #001F3F 0%, #003D7A 55%, #0071C5 120%);
    color: #fff;
    border-radius: 20px;
    padding: 2rem 1.75rem;
    position: relative;
    overflow: hidden;
}

.upload-info-panel::before {
    content: "";
    position: absolute;
    inset: 0;
    background-image: radial-gradient(rgba(255, 255, 255, 0.12) 1px, transparent 1px);
    background-size: 26px 26px;
    opacity: 0.4;
    pointer-events: none;
}

.info-hero-icon {
    width: 60px;
    height: 60px;
    border-radius: 16px;
    background: rgba(255, 255, 255, 0.14);
    border: 1px solid rgba(255, 255, 255, 0.2);
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 1.1rem;
    position: relative;
    z-index: 2;
}

.info-title {
    font-family: var(--font-display);   /* hero/section title */
    font-size: 1.6rem;                   /* a touch larger for presence */
    line-height: 1.25;
    font-weight: 700;
    /* Panel has a dark gradient background — title MUST be white to be legible */
    color: #ffffff !important;
    letter-spacing: -0.01em;
    margin: 0 0 0.6rem;
    position: relative;
    z-index: 2;
}

.info-desc {
    font-size: 0.95rem;
    line-height: 1.6;
    color: rgba(255, 255, 255, 0.88) !important;
    margin: 0 0 1.75rem;
    position: relative;
    z-index: 2;
}

.info-steps {
    list-style: none;
    margin: 0 0 1.75rem;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    position: relative;
    z-index: 2;
}

.info-steps li {
    display: flex;
    align-items: center;
    gap: 0.85rem;
    text-align: left;
}

.step-no {
    width: 30px;
    height: 30px;
    flex-shrink: 0;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.16);
    border: 1px solid rgba(255, 255, 255, 0.25);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 0.85rem;
    box-sizing: border-box;
    padding-left: 10px;
    padding-top: 6px;
}

.info-steps li > div {
    flex: 1;
    min-width: 0;
    text-align: left;
}

.info-steps strong {
    display: block;
    font-size: 0.9rem;
    font-weight: 700;
    text-align: left;
}

.info-steps span {
    display: block;
    font-size: 0.78rem;
    opacity: 0.72;
    margin-top: 0.1rem;
    text-align: left;
}

.info-meta {
    display: flex;
    gap: 0.6rem;
    flex-wrap: wrap;
    position: relative;
    z-index: 2;
}

.meta-pill {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    background: rgba(255, 255, 255, 0.12);
    border: 1px solid rgba(255, 255, 255, 0.18);
    padding: 0.4rem 0.85rem;
    border-radius: 20px;
    font-size: 0.78rem;
    font-weight: 600;
}

.upload-action-panel {
    background: #fff;
    border: 1px solid #eef2f7;
    border-radius: 20px;
    padding: 1.75rem;
    box-shadow: 0 6px 20px rgba(15, 23, 42, 0.04);
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
    min-width: 0;
}

.field-block {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.field-label {
    font-size: 0.82rem;
    font-weight: 700;
    color: #334155;
}

.req { color: #ef4444; }

.select-wrap {
    position: relative;
    display: flex;
    align-items: center;
}

.select-icon {
    position: absolute;
    left: 1rem;
    color: #64748b;
    pointer-events: none;
}

.pro-input {
    width: 100%;
    padding: 0.85rem 1rem 0.85rem 2.75rem;
    border-radius: 12px;
    border: 1.5px solid #e2e8f0;
    background-color: #f8fafc;
    color: #1e293b;
    font-family: inherit;
    font-size: 0.92rem;
    font-weight: 500;
    outline: none;
    cursor: pointer;
    transition: all 0.2s;
}

.pro-input:focus {
    background-color: #fff;
    border-color: #0071C5;
    box-shadow: 0 0 0 4px rgba(0, 113, 197, 0.1);
}

.input-error {
    border-color: #ef4444 !important;
    box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1) !important;
}

.pro-dropzone {
    border: 2px dashed #cbd5e1;
    background: #f8fafc;
    border-radius: 16px;
    padding: 2.25rem 1.5rem;
    text-align: center;
    cursor: pointer;
    transition: all 0.25s;
    position: relative;
    overflow: hidden;
}

.pro-dropzone:hover {
    border-color: #0071C5;
    background: #f0f9ff;
}

.pro-dropzone.is-dragging {
    border-color: #0071C5;
    background: rgba(0, 113, 197, 0.08);
    box-shadow: 0 0 0 4px rgba(0, 113, 197, 0.1) inset;
    transform: scale(1.005);
}

.dz-scanline {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 3px;
    background: linear-gradient(90deg, transparent, #0071C5, transparent);
    box-shadow: 0 0 14px #0071C5;
    animation: dzScan 1.6s linear infinite;
}

@keyframes dzScan {
    0% { top: 0; opacity: 0; }
    15% { opacity: 1; }
    85% { opacity: 1; }
    100% { top: 100%; opacity: 0; }
}

.dz-icon {
    width: 64px;
    height: 64px;
    margin: 0 auto 0.9rem;
    border-radius: 50%;
    background: rgba(0, 113, 197, 0.1);
    color: #0071C5;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: transform 0.25s;
}

.pro-dropzone:hover .dz-icon,
.pro-dropzone.is-dragging .dz-icon {
    transform: translateY(-4px);
}

.dz-primary {
    margin: 0;
    font-size: 1rem;
    font-weight: 700;
    color: #1e293b;
}

.dz-secondary {
    margin: 0.4rem 0;
    font-size: 0.8rem;
    color: #64748b;
}

.dz-browse {
    background: #fff;
    border: 1.5px solid #0071C5;
    color: #0071C5;
    font-weight: 700;
    font-size: 0.88rem;
    padding: 0.6rem 1.4rem;
    border-radius: 10px;
    cursor: pointer;
    transition: all 0.2s;
    font-family: var(--font-sans);
}

.dz-browse:hover {
    background: #0071C5;
    color: #fff;
}

.dz-hint {
    margin: 1rem 0 0;
    font-size: 0.75rem;
    color: #64748b;
    font-weight: 500;
}

.files-block {
    display: flex;
    flex-direction: column;
    gap: 0.6rem;
}

.files-block-head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 0.82rem;
    font-weight: 700;
    color: #475569;
}

.clear-all {
    background: none;
    border: none;
    color: #b42318;
    font-weight: 700;
    font-size: 0.78rem;
    cursor: pointer;
    font-family: var(--font-sans);
}

.clear-all:hover { text-decoration: underline; }

.file-cards {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    max-height: 200px;
    overflow-y: auto;
}

.file-cards::-webkit-scrollbar { width: 5px; }
.file-cards::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }

.file-card-row {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    padding: 0.65rem 0.85rem;
    background: #f8fafc;
    border: 1px solid #eef2f7;
    border-radius: 12px;
    transition: all 0.2s;
    animation: fileIn 0.25s ease;
    min-width: 0;
}

@keyframes fileIn {
    from { opacity: 0; transform: translateX(-6px); }
    to { opacity: 1; transform: translateX(0); }
}

.file-card-row:hover {
    border-color: rgba(0, 113, 197, 0.3);
    background: #f0f9ff;
}

.fc-icon {
    width: 38px;
    height: 38px;
    flex-shrink: 0;
    border-radius: 10px;
    background: #fef3f2;
    color: #b42318;
    display: flex;
    align-items: center;
    justify-content: center;
}

.fc-info {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
}

.fc-name {
    font-size: 0.85rem;
    font-weight: 600;
    color: #1e293b;
    max-width: 100%;
    white-space: normal;
    overflow-wrap: anywhere;
    word-break: break-word;
    line-height: 1.35;
}

.fc-size {
    font-size: 0.72rem;
    color: #64748b;
    font-weight: 500;
}

.fc-remove {
    width: 30px;
    height: 30px;
    flex-shrink: 0;
    border: none;
    border-radius: 8px;
    background: transparent;
    color: #64748b;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
}

.fc-remove:hover {
    background: #fef3f2;
    color: #b42318;
}

.pro-submit {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    width: 100%;
    border: none;
    background: #0071C5;
    color: #fff;
    padding: 1rem;
    border-radius: 12px;
    font-size: 0.95rem;
    font-weight: 700;
    font-family: var(--font-sans);
    cursor: pointer;
    box-shadow: 0 6px 16px rgba(0, 113, 197, 0.3);
    transition: all 0.2s;
    margin-top: auto;
}

.pro-submit:hover:not(:disabled) {
    background: #005a9e;
    transform: translateY(-2px);
    box-shadow: 0 10px 22px rgba(0, 113, 197, 0.4);
}

.pro-submit:disabled {
    opacity: 0.55;
    cursor: not-allowed;
    box-shadow: none;
}

.spin { animation: spin 1s linear infinite; }

@keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
}

@media (max-width: 920px) {
    .upload-pro-grid { grid-template-columns: 1fr; }
}
</style>
